import api, {
    ResponseAndErrorCallback,
    ResponseCallback
} from '../services/api.service'
import { Gs1Config } from '../views/LabelBuilder/LabelBuilder.slice'

export type Gs1CarrierMode = 'gtin' | 'ai' | 'dl'

export interface Gs1Carrier {
    id: string
    name: string
    group: string
    mode: Gs1CarrierMode
    length?: number
    max?: number
    key?: string
    capacity: string
    use: string
    textInSymbol: boolean
}

export interface Gs1Ai {
    ai: string
    title: string
    format: string
    category: string
    fnc1: boolean
    isDate: boolean
    requires: string
    excludes: string[]
    digitalLink: string | null
}

export interface Gs1Catalog {
    enabled: boolean
    dictionaryVersion: string
    carriers: Gs1Carrier[]
    ais: Gs1Ai[]
}

export interface Gs1Fix {
    label: string
    value: string
}

export interface Gs1ElementResult {
    ai: string
    value?: string
    note?: string
    error?: string
    code?: string
    fix?: Gs1Fix
}

export interface Gs1Issue {
    level: 'error' | 'warning'
    code: string
    message: string
    ai?: string
    add?: string[]
}

export interface Gs1CheckResult {
    carrier: string
    valid: boolean
    elements: Gs1ElementResult[]
    issues: Gs1Issue[]
    text: string | null
    hri: string | null
    encoded: string | null
    resolved: string[]
    templateIssues: string[]
    available: string[]
    preview: { url: string, width: number, height: number } | null
    epc?: Gs1Epc
}

export interface Gs1Epc {
    scheme?: string
    bits?: number
    hex?: string
    uri?: string
    error?: string
}

export interface Gs1CheckRequest {
    gs1: Gs1Config
    dpi: number
}

const routes = {
    catalog: '/gs1/catalog',
    check: '/gs1/validate'
}

let catalogCache: Gs1Catalog | null = null
let catalogWaiting: Array<ResponseCallback<Gs1Catalog>> = []

/** Display name of a barcode type, once the catalog has loaded. */
export function gs1CarrierName (id: string): string {
  return catalogCache?.carriers.find(carrier => carrier.id === id)?.name || id
}

const gs1Api = {
    /**
     * Barcode types and Application Identifiers. Loaded once per page and
     * shared, since every GS1 panel and the element tray need it.
     */
    catalog (callback: ResponseCallback<Gs1Catalog>) {
        if (catalogCache) {
            callback(catalogCache)
            return
        }
        catalogWaiting.push(callback)
        if (catalogWaiting.length > 1) return
        api.getAndError<Gs1Catalog>(routes.catalog, {}, catalog => {
            catalogCache = catalog
            catalogWaiting.forEach(waiting => waiting(catalog))
            catalogWaiting = []
        }, () => {
            catalogWaiting.forEach(waiting => waiting(null))
            catalogWaiting = []
        })
    },

    check (
        payload: Gs1CheckRequest,
        callback: ResponseCallback<Gs1CheckResult>,
        errorCallback: ResponseAndErrorCallback
    ) {
        api.postAndError(routes.check, payload, callback, errorCallback)
    }
}

export default gs1Api
